import {
  BodyR,
  Button,
  DetailB,
  HeadingRegular as Heading3,
  TextField,
} from '@pensionbee/honeypot-ui';
import { ThemedImage } from '@/domains/theme/ThemedImage';
import { useState } from 'react';

import { emailSchema } from '@/domains/account-management/partials/forgotPassword/forgotPasswordPartial/ForgotPasswordPartial';
import Link from 'next/link';
import { useAuthContext } from '@/domains/auth/authContext';

type Props = {
  emailAddress: string;
  password: string;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onEmailPasswordSubmit: () => Promise<void>;
  errorMessage?: string | React.ReactNode;
  showPassword: boolean;
  onChangeShowPassword: (show: boolean) => void;
};

export const LoginFormPartial = ({
  emailAddress,
  password,
  onEmailChange,
  onPasswordChange,
  onEmailPasswordSubmit,
  errorMessage,
  showPassword,
  onChangeShowPassword,
}: Props) => {
  const [invalidEmailError, setInvalidEmailError] = useState<
    string | undefined
  >(undefined);

  const { authReady, isLoggedIn } = useAuthContext();

  const handleClickShowPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onChangeShowPassword(!showPassword);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onEmailChange(value);
    setInvalidEmailError(undefined);

    const validEmail = emailSchema.safeParse(value);
    if (!validEmail.success) {
      setInvalidEmailError(validEmail.error.issues[0].message);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onPasswordChange(value);
  };

  if (!authReady || isLoggedIn) {
    return null;
  }

  return (
    <form
      onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await onEmailPasswordSubmit();
      }}
    >
      <Heading3 className="pb-20" as="h1">
        Welcome back
      </Heading3>
      <BodyR>
        Good to see you back. Don&apos;t have an account yet?{' '}
        <BodyR
          as={Link}
          data-testid="sign-up-link"
          className="text-blue-100 underline hover:cursor-pointer focus:border-2 focus:border-solid focus:border-blue-100"
          href="/hive/accounts/signup"
        >
          Sign up
        </BodyR>
      </BodyR>
      {errorMessage && (
        <div className="bg-red-10 rounded-12 p-20 mt-20">
          <DetailB className="text-red-140">{errorMessage}</DetailB>
        </div>
      )}
      <div className="pt-28">
        <TextField
          label="Email address"
          aria-label="email"
          type="email"
          name="email"
          autoComplete="username"
          value={emailAddress}
          onChange={handleEmailChange}
          error={invalidEmailError}
          required
        />
        <TextField
          label="Password"
          aria-label="password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={password}
          onChange={handlePasswordChange}
          required
          autoComplete="current-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          suffix={
            <Button
              variant="tertiary"
              className="p-0 bg-transparent hover:bg-transparent focus:bg-transparent"
              aria-label="Toggle password visibility"
              onClick={handleClickShowPassword}
              type="button"
            >
              <ThemedImage
                className="h-24"
                name={showPassword ? 'icon-hide' : 'icon-show'}
                alt={showPassword ? 'Hide password icon' : 'Show password icon'}
              />
            </Button>
          }
        />
      </div>
      <div className="flex flex-col gap-20">
        <Button
          className="w-full"
          type="submit"
          disabled={!!invalidEmailError || !emailAddress || !password}
        >
          Log in
        </Button>
        <Button
          className="w-full"
          variant="secondary"
          as={Link}
          data-testid="forgot-password-link"
          href={'/hive/accounts/forgot-password'}
        >
          Forgotten password
        </Button>
      </div>
    </form>
  );
};
